import { useTranslation } from 'react-i18next'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Trash, Lock } from '@phosphor-icons/react'
import type { GranularityApproach } from '@/feature/financial-model/ecommerce/revenues/api/types'
import {
  Button,
  SelectValue,
  SelectTrigger,
  Select,
  SelectItem,
  SelectItemWithIcon,
  SelectContent,
  Input,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormField
} from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import {
  useDeleteCategoryMutation,
  useGetCogsDirectCostsCategories,
  usePostCategoryMutation,
  usePostUnitsSettings,
  useSuspenseUnitsSettings,
  useUpdateCategoryMutation
} from '@/feature/financial-model/ecommerce/cogs/api'
import { Container } from '@/components/shared/Container'
import {
  DirectCostsApproach,
  DirectCostsCategory,
  DirectCostsGranularity,
  UnitSettingPostRequest
} from '@/feature/financial-model/ecommerce/cogs/api/types.ts'
import { cn } from '@/lib/utils'
import { CardBox } from '@/components/shared/cards/SelectCards'
import { DetailCategoryProductsTable } from '@/feature/financial-model/ecommerce/cogs/components/detail-category-products-table.tsx'
import { TableActionPopoverProvider } from '@/components/shared/tables/popovers.tsx'
import { useGetSingleFinancialModelSettingsV2Query } from '@/feature/financial-model/api'

export const DetailedCategoriesSkeleton = () => (
  <div className='flex flex-col gap-6'>
    {[1, 2, 3].map(value => (
      <Skeleton key={`item_${value}`} className='h-[225px] w-full rounded-md' />
    ))}
  </div>
)

const AddCategoryButton = ({ financialModelId }: { financialModelId: string }) => {
  const { t } = useTranslation(`cogs`)
  const { mutate: addCategory, isPending } = usePostCategoryMutation(financialModelId)

  return (
    <Button
      variant='primary'
      onClick={() => addCategory({ financialModelId, name: `` })}
      disabled={isPending}
      className={cn(isPending && `pointer-events-none cursor-not-allowed`)}
    >
      <Plus size={16} className='mr-2' weight='bold' />
      {t(`cogs.set_up.direct_costs.detailed.add_btn`)}
    </Button>
  )
}
const DeleteCategoryButton = ({ financialModelId, id: categoryId }: { financialModelId: string; id: string }) => {
  const { mutate: deleteCategory, isPending } = useDeleteCategoryMutation({
    financialModelId,
    categoryId
  })

  return (
    <Button
      variant='ghost'
      onClick={() => deleteCategory({ financialModelId, categoryId })}
      disabled={isPending}
      className={cn(isPending && `pointer-events-none cursor-not-allowed`)}
    >
      <Trash size={20} format={`stroke`} weight={`light`} className={`text-secondary`} />
    </Button>
  )
}

const CategoryNameField = ({ id, name, financialModelId }: DirectCostsCategory & { financialModelId: string }) => {
  const { t } = useTranslation(`cogs`)
  const { mutate: updateCategoryName } = useUpdateCategoryMutation(financialModelId)
  const form = useForm({
    resolver: zodResolver(
      z.object({
        name: z.string()
      })
    ),
    defaultValues: {
      name
    }
  })
  const [debouncedEditingName, updateDebouncedEditingName] = useDebounceValue(name, 300, { trailing: true })

  useEffect(() => {
    updateCategoryName({
      financialModelId,
      name: debouncedEditingName,
      categoryId: id
    })
  }, [id, debouncedEditingName, financialModelId, updateCategoryName])

  const { setValue } = form

  return (
    <CardBox variant='left'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(values => {
            updateCategoryName({
              financialModelId,
              name: values.name,
              categoryId: id
            })
          })}
        >
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor={field.name}>{t(`cogs.set_up.direct_costs.detailed.name.label`)}</FormLabel>
                <FormControl>
                  <Input
                    autoFocus
                    type='text'
                    {...field}
                    className={`mt-0 h-10`}
                    onChange={e => {
                      setValue(`name`, e.target.value)
                      updateDebouncedEditingName(e.target.value)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </CardBox>
  )
}

const GranularityApproachFields = ({ name, id, financialModelId }: DirectCostsCategory & { financialModelId: string }) => {
  const { t } = useTranslation(`cogs`)
  const [rerenderKey, setRerenderKey] = useState(0)
  const { data: unitsSettings } = useSuspenseUnitsSettings(financialModelId, id)
  const { mutate: updateUnitsSettings } = usePostUnitsSettings(financialModelId, id)

  const { data: setUpSettings } = useGetSingleFinancialModelSettingsV2Query({
    id: financialModelId!,
    module: `revenue`,
    section: `setup`
  })

  const modelGranularityApproach = useMemo(
    () => setUpSettings?.revenue.settings.find(setting => setting.type === `revenue_level_approach`)?.value as GranularityApproach,
    [setUpSettings]
  )

  const handleGranularityUpdate = useCallback(
    (value: DirectCostsGranularity) => {
      if (value === unitsSettings.level_of_granularity) return
      const objToUpdate: UnitSettingPostRequest = {
        level_of_granularity: value,
        modeling_approach: unitsSettings.modeling_approach ?? null,
        unit_settings: unitsSettings.unit_settings,
        category_id: id
      }
      if (value === `by_product`) {
        objToUpdate.modeling_approach = `cost_per_unit`
        setRerenderKey(prev => prev + 1)
      }
      updateUnitsSettings(objToUpdate)
    },
    [id, unitsSettings.level_of_granularity, unitsSettings.modeling_approach, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const handleApproachUpdate = useCallback(
    (value: DirectCostsApproach) => {
      if (value === unitsSettings.modeling_approach) return
      const objToUpdate: UnitSettingPostRequest = {
        modeling_approach: value,
        level_of_granularity: unitsSettings.level_of_granularity!,
        unit_settings: unitsSettings.unit_settings,
        category_id: id
      }
      updateUnitsSettings(objToUpdate)
    },
    [id, unitsSettings.level_of_granularity, unitsSettings.modeling_approach, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const granularity: Record<DirectCostsGranularity, string> = useMemo(
    () => ({
      aggregate: t(`cogs.set_up.direct_costs.granularity.aggregate`),
      by_product: t(`cogs.set_up.direct_costs.granularity.product`),
      detailed: t(`cogs.set_up.direct_costs.granularity.detailed`)
    }),
    [t]
  )

  const approach: Record<DirectCostsApproach, string> = useMemo(
    () => ({
      absolute_value: t(`cogs.set_up.direct_costs.approach.absolute`),
      cost_per_unit: t(`cogs.set_up.direct_costs.approach.x_unit`),
      percentage_of_revenues: t(`cogs.set_up.direct_costs.approach.x_percentage`)
    }),
    [t]
  )

  const computedGranularity = useMemo(() => {
    if (unitsSettings.level_of_granularity === 'by_product' && modelGranularityApproach === 'order_level') {
      return 'aggregate'
    }
    return unitsSettings.level_of_granularity
  }, [unitsSettings.level_of_granularity, modelGranularityApproach])

  const modellingApproach = computedGranularity === `by_product` ? `cost_per_unit` : (unitsSettings.modeling_approach ?? undefined)

  return (
    <>
      <CardBox variant='left'>
        <Select value={computedGranularity ?? undefined} onValueChange={handleGranularityUpdate} {...(!name && { disabled: true })}>
          <div className='mb-2 flex items-center gap-2'>
            <p>{t(`cogs.set_up.direct_costs.granularity.label`)}</p>
          </div>
          <SelectTrigger>
            <SelectValue placeholder='Select Granularity' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='aggregate'>{granularity.aggregate}</SelectItem>
            <SelectItemWithIcon
              value='by_product'
              icon={modelGranularityApproach === 'order_level' ? <Lock className='opacity-50' /> : undefined}
              iconPosition='right'
              disabled={modelGranularityApproach === 'order_level'}
            >
              {granularity.by_product}
            </SelectItemWithIcon>
          </SelectContent>
        </Select>
      </CardBox>
      <CardBox variant='left'>
        <Select key={rerenderKey} value={modellingApproach} onValueChange={handleApproachUpdate}>
          <div className='mb-2 flex items-center gap-2'>
            <p
              className={cn(`font-medium`, {
                'text-disabled': !computedGranularity || computedGranularity === `by_product`
              })}
            >
              {t(`cogs.set_up.direct_costs.approach.label`)}
            </p>
          </div>
          <SelectTrigger disabled={!computedGranularity || computedGranularity === `by_product`}>
            <SelectValue placeholder='Select Approach' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='absolute_value'>{approach.absolute_value}</SelectItem>
            <SelectItemWithIcon
              value='cost_per_unit'
              icon={modelGranularityApproach === 'order_level' ? <Lock className='opacity-50' /> : undefined}
              iconPosition='right'
              disabled={modelGranularityApproach === 'order_level'}
            >
              {approach.cost_per_unit}
            </SelectItemWithIcon>
            <SelectItem value='percentage_of_revenues'>{approach.percentage_of_revenues}</SelectItem>
          </SelectContent>
        </Select>
      </CardBox>
    </>
  )
}

const DEFAULT_SELECTED: string[] = []

const CategoryItem = (categoryAndId: DirectCostsCategory & { financialModelId: string }) => (
  <Container className='min-h-0'>
    <div className={`mb-4 flex justify-end`}>
      <DeleteCategoryButton {...categoryAndId} />
    </div>
    <div className={`grid grid-cols-3 gap-8`}>
      <CategoryNameField {...categoryAndId} />
      <Suspense
        fallback={
          <>
            <CardBox variant='left'>
              <Skeleton className={`h-6 w-56`} />
              <Skeleton className={`h-10`} />
            </CardBox>
            <CardBox variant='left'>
              <Skeleton className={`h-6 w-56`} />
              <Skeleton className={`h-10`} />
            </CardBox>
          </>
        }
      >
        <GranularityApproachFields {...categoryAndId} />
      </Suspense>
      <Suspense fallback={<Skeleton className={`h-0`} />}>
        <div className={`col-span-3`}>
          <TableActionPopoverProvider defaultSelected={DEFAULT_SELECTED}>
            <DetailCategoryProductsTable categoryId={categoryAndId.id} financialModelId={categoryAndId.financialModelId} />
          </TableActionPopoverProvider>
        </div>
      </Suspense>
    </div>
  </Container>
)

const CategoryList = ({ financialModelId }: { financialModelId: string }) => {
  const { data: categoriesData } = useGetCogsDirectCostsCategories(financialModelId)
  return (
    <div className={`flex flex-col gap-6 rounded-xl bg-gray-10 p-6`}>
      {categoriesData.categories.map(category => (
        <CategoryItem key={category.id} {...category} financialModelId={financialModelId} />
      ))}
      <div className={`flex`}>
        <AddCategoryButton financialModelId={financialModelId} />
      </div>
    </div>
  )
}

export const DetailedCategories = ({ financialModelId }: { financialModelId: string }) => {
  const { t } = useTranslation(`cogs`)

  return (
    <Container className='mt-5 min-h-0'>
      <p className={`text-medium mb-2 text-xl text-primary`}>{t(`cogs.set_up.direct_costs.detailed.title`)}</p>
      <p className={`mb-8 text-base text-secondary`}>{t(`cogs.set_up.direct_costs.detailed.subtitle`)}</p>
      <Suspense fallback={<DetailedCategoriesSkeleton />}>
        <CategoryList financialModelId={financialModelId} />
      </Suspense>
    </Container>
  )
}
